import getFileKey from './getFileKey'
import getFileDimensions, { RESIZED_FILE_TYPES, type S3UrlComponents } from './getFileDimensions'
import getPageCount from './getPageCount'
import getUploadData from './getUploadData'
import compressFile, { type CompressFileOptions, type CompressFallbackReason } from './compressFile'
import generateVideoThumbnail from './generateVideoThumbnail'
import getRequiredTask from './getRequiredTask'
import getUploadUrl from './getUploadUrl'
import getMp4ThumbnailKey from './getMp4ThumbnailKey'
import dataUriToBlob from './dataUriToBlob'
import getInitialImageWidth from './getInitialImageWidth'

export type { CompressFileOptions, CompressFallbackReason, S3UrlComponents }

const PERMITTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  // 'image/webp',
  'application/pdf',
  'video/*'
]

export function isFilePdf(file: { format?: string; type?: string; src?: string }): boolean {
  return (
    file?.format === 'pdf' || file?.type === 'application/pdf' || (!!file?.src && /\.pdf(?:\?.*)?$/i.test(file.src))
  )
}

// Extensions we treat as video. Mirrors VIDEO_FORMATS in
// @paintscout/ui FileViewer/file-type-utils.ts so the list thumbnail and the
// media viewer agree on what counts as a video.
const VIDEO_FORMATS = ['mp4', 'mov', 'webm', 'm4v', 'ogv', 'avi', 'mkv']

/**
 * Whether a file is a video. Uploaded videos (mp4/mov) go through Cloudinary
 * (see uploadFile.ts) and carry `format` (e.g. 'mp4') + a `src` secure_url, so we
 * match on the format first, then a known extension, then the extension on `src`.
 * Kept beside isFilePdf so callers (e.g. FilesDocList) can decide how to render a
 * file without reaching into ui internals.
 */
export function isFileVideo(file: { format?: string; type?: string; src?: string }): boolean {
  if (!file) return false
  if (file.format?.startsWith('video') || file.type?.startsWith('video')) return true
  if (file.format && VIDEO_FORMATS.includes(file.format.toLowerCase())) return true
  if (!file.src) return false
  const ext = file.src.split('?')[0]?.split('.').pop()?.toLowerCase() ?? ''
  return VIDEO_FORMATS.includes(ext)
}

export interface S3FileUploadData {
  size: number
  src: string
  s3PublicKey: string
  bucket: string
  format: string
  type: string
  pages?: number
  width?: number
  height?: number
}

export {
  PERMITTED_FILE_TYPES,
  RESIZED_FILE_TYPES,
  compressFile,
  getFileKey,
  getUploadData,
  getPageCount,
  getFileDimensions,
  dataUriToBlob,
  getMp4ThumbnailKey,
  getUploadUrl,
  getRequiredTask,
  generateVideoThumbnail,
  getInitialImageWidth
}
